'use client';

import { useState } from 'react';
import Link from 'next/link';
import { signOut } from 'next-auth/react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useMutation } from '@apollo/client/react';
import {
  REGISTRATION_LOOKUP, SELECT_REGISTRATION_EMAIL,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import {
  useCurrentUser, hasPortalAccess,
} from '@paeffgen/react/crm-documents/feature-auth';
import { mapRegistrationError as mapError } from './registration-errors';

/** Primärer Button; inaktiv = grau (statt transparentes Orange). */
const PRIMARY_BTN =
  'w-full rounded-lg bg-[color:var(--color-primary)] px-4 py-3 font-medium text-white ' +
  'disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-500';

interface MaskedEmail { id: string; masked: string; alreadyAssigned: boolean }
interface LookupData {
  registrationLookup: { lookupToken: string; maskedEmails: MaskedEmail[] };
}

export function RegisterFlow() {
  const [step, setStep] = useState<'INPUT' | 'CHOOSE' | 'SENT'>('INPUT');
  const [customerNumber, setCustomerNumber] = useState('');
  const [lookupToken, setLookupToken] = useState('');
  const [emails, setEmails] = useState<MaskedEmail[]>([]);
  const [chosen, setChosen] = useState('');
  const [error, setError] = useState('');

  const [lookup, { loading: lookingUp }] = useMutation<LookupData>(REGISTRATION_LOOKUP);
  const [selectEmail, { loading: sending }] = useMutation(SELECT_REGISTRATION_EMAIL);

  // A user who is logged in but lacks portal access (no customerNumber and no
  // privileged role) was routed here by the middleware to link their Kundennummer.
  // Show an explanation and offer logout instead of the "back to login" link,
  // which the middleware would just bounce back to /register.
  const { user, isAuthenticated } = useCurrentUser();
  const needsLink = isAuthenticated && !hasPortalAccess(user);

  const onLookup = async () => {
    setError('');
    try {
      const { data } = await lookup({ variables: { customerNumber: customerNumber.trim() } });
      const result = data?.registrationLookup;
      if (!result) { setError(mapError()); return; }
      setLookupToken(result.lookupToken);
      setEmails(result.maskedEmails);
      setStep('CHOOSE');
    } catch (e) {
      setError(mapError((e as { message?: string }).message));
    }
  };

  const onSelect = async () => {
    setError('');
    try {
      await selectEmail({ variables: { lookupToken, emailId: chosen } });
      setStep('SENT');
    } catch (e) {
      setError(mapError((e as { message?: string }).message));
    }
  };

  return (
    <div className="text-left">
      <h2 className="mb-6 text-xl font-semibold">Registrieren</h2>

      {needsLink && (
        <div
          role="status"
          className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
        >
          <p className="font-semibold">
            Ihr Account ist noch nicht für das Kundenportal freigeschaltet.
          </p>
          <p className="mt-1">
            Geben Sie einmalig Ihre Kundennummer (Debitorennummer) ein, um Ihren
            Zugang zu Belegen, Artikeln und Materialanfragen zu aktivieren.
          </p>
        </div>
      )}

      {error && <p className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      {step === 'INPUT' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium" htmlFor="custno">Kundennummer (Debitorennummer)</label>
            <input
              id="custno" value={customerNumber} placeholder="z. B. DE0123456"
              onChange={(e) => setCustomerNumber(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-zinc-300 px-3 py-2"
            />
            <p className="mt-1.5 text-sm text-[color:var(--color-text-muted)]">
              Die Nummer finden Sie oben rechts auf jeder Rechnung.
            </p>
          </div>
          <button
            type="button" disabled={!customerNumber.trim() || lookingUp} onClick={onLookup}
            className={`flex items-center justify-center gap-2 ${PRIMARY_BTN}`}
          >
            Weiter <ArrowRight size={18} aria-hidden="true" />
          </button>
        </div>
      )}

      {step === 'CHOOSE' && (
        <div className="space-y-4">
          <p className="text-sm text-[color:var(--color-text-muted)]">
            Wählen Sie die E-Mail-Adresse, an die wir den Bestätigungslink senden sollen:
          </p>
          <fieldset className="space-y-2">
            {emails.map((em) => (
              <label
                key={em.id}
                className={`flex items-center gap-2 ${em.alreadyAssigned ? 'cursor-not-allowed text-zinc-400' : ''}`}
              >
                <input
                  type="radio" name="email" aria-label={em.masked}
                  disabled={em.alreadyAssigned}
                  checked={chosen === em.id} onChange={() => setChosen(em.id)}
                />
                <span>
                  {em.masked}
                  {em.alreadyAssigned && (
                    <span className="ml-2 text-xs">
                      — bereits zugeordnet, bitte melden Sie sich mit dieser Adresse an
                    </span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
          {emails.length > 0 && emails.every((em) => em.alreadyAssigned) && (
            <div
              role="status"
              className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
            >
              <p className="font-semibold">
                Alle hinterlegten E-Mail-Adressen sind bereits einem Zugang zugeordnet.
              </p>
              <p className="mt-1">
                Eine erneute Registrierung ist nicht möglich — bitte melden Sie sich mit dem
                bestehenden Konto an (ggf. „Passwort vergessen“ nutzen).
              </p>
            </div>
          )}
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Es können nur E-Mail-Adressen verwendet werden, die bereits in unserem System zu
            Ihrer Kundennummer hinterlegt sind. Möchten Sie eine andere Adresse hinterlegen
            lassen, wenden Sie sich bitte an Ihren Ansprechpartner.
          </p>
          <button
            type="button" disabled={!chosen || sending} onClick={onSelect}
            className={PRIMARY_BTN}
          >
            Bestätigungsmail senden
          </button>
        </div>
      )}

      {step === 'SENT' && (
        <p className="text-center">
          Wir haben Ihnen eine E-Mail gesendet. Bitte prüfen Sie Ihr <strong>Postfach</strong> und
          klicken Sie auf den Bestätigungslink.
        </p>
      )}

      {needsLink ? (
        <button
          type="button"
          onClick={() => signOut({ callbackUrl: '/login' })}
          className="mt-6 flex w-full items-center justify-center gap-1.5 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-secondary)]"
        >
          <ArrowLeft size={16} aria-hidden="true" /> Abmelden
        </button>
      ) : (
        <Link
          href="/login"
          className="mt-6 flex items-center justify-center gap-1.5 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-secondary)]"
        >
          <ArrowLeft size={16} aria-hidden="true" /> Zurück zur Anmeldung
        </Link>
      )}
    </div>
  );
}
